import { bind, classNames, h, tag } from "omi";

import { NWAccordion, NwAccordionItem } from "@/components/atoms/nw-accordion";
import { BoxStatus } from "@/components/validations/models";
import { IntersectionEvents } from "@/services/intersectionService/events";
import { PureComponent } from "@/utils/pureComponent";
import { NWIcon } from "@/components";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

import { STEP_TOC_UPDATED_EVENT, StepTocStructure } from "../../../../editor/extensions/tableOfContents/models";
import { StatusIndicator } from "./ui/statusIndicator";
import { getChildDisplayType, getStatusLabel, TocItem } from "./models";
import { useTocStore } from "./store";

/**
 * Properties for the TableOfContents component
 */
type Props = {
  /**
   * Whether to watch for intersection events
   */
  withIntersectionObservation?: boolean;

  /**
   * Callback function to handle marker selection
   */
  onItemSelect: (itemId: string) => void;
};

@tag("pages-project-ui-table-of-content")
export class NWTableOfContents extends PureComponent<Props> {
  static css = [tailwind];

  private store = useTocStore();

  installed() {
    document.addEventListener(STEP_TOC_UPDATED_EVENT, this.handleTocUpdate);

    if (this.props.withIntersectionObservation) {
      addEventListener(IntersectionEvents.IntersectionObserved, this.handleIntersectionEvent, {
        passive: true,
      });
    }
  }

  uninstall() {
    document.removeEventListener(STEP_TOC_UPDATED_EVENT, this.handleTocUpdate);

    if (this.props.withIntersectionObservation) {
      document.removeEventListener(IntersectionEvents.IntersectionObserved, this.handleIntersectionEvent);
    }
  }

  @bind
  private handleTocUpdate(event: Event) {
    const { structure } = (event as CustomEvent<{ structure: StepTocStructure }>).detail;
    this.store.updateFromStructure(structure);
  }

  @bind
  private handleIntersectionEvent(event: Event) {
    const { target, isIntersecting } = (event as CustomEvent<IntersectionObserverEntry>).detail;

    const tag = target.tagName.toLowerCase();

    if (tag === "nw-step" && isIntersecting) {
      this.store.setFocusedStep(target.id);
    }

    if (tag === "nw-section" && isIntersecting) {
      const stepId = target.getAttribute("stepid") || target.id;
      this.store.setFocusedStep(stepId);
    }

    if (tag === "nw-validation-box" && isIntersecting) {
      this.store.setFocusedChild(isIntersecting ? target.id : undefined);
    }
  }

  @bind
  private togglePin() {
    const desiredState = !this.store.isPinned.value;
    this.store.setPinned(desiredState);

    import("@/services/analyticsService").then(({ useAnalyticsService }) => {
      useAnalyticsService().trackPinToggled(
        Store.project.id.value!,
        "table-of-content",
        desiredState ? "pinned" : "unpinned",
      );
    });
  }

  @bind
  private handleAccordionChange(id: string, isOpen: boolean) {
    this.store.toggleStep(id, isOpen);
  }

  @bind
  private handleItemSelected(itemType: "step" | "validation_box" | "bookmark" | "generic", itemId: string) {
    if (!itemId) return;

    import("@/services/analyticsService").then(({ useAnalyticsService }) => {
      useAnalyticsService().trackJumpTo(itemType, Store.project.id.value!, itemId);
    });

    this.props.onItemSelect(itemId);
  }

  @bind
  private renderList(items: TocItem[]) {
    return items.map(({ id, title, description, children, isOpen, childrenVisibility, isBookmark }) => {
      const hasLatestBookmarkedChild = children.some(
        child => child.itemType === "bookmark" && child.id === this.store.lastestBookmarkNodeId.value,
      );

      return (
        <div key={id}>
          <NWAccordion
            type="controlled"
            isOpen={isOpen || hasLatestBookmarkedChild}
            onChange={(isOpen: boolean) => this.handleAccordionChange(id, isOpen)}
            textSize="sm"
            label={`${title}${description ? `: ${description}` : ""}`}
            textColor={this.store.focusedStepId.value === id ? "dark-gray" : "light-grey"}
            active={this.store.focusedStepId.value === id}
            onLabelClick={() => this.handleItemSelected("step", id)}
            icon={isBookmark ? "bookmark" : undefined}
          >
            {children.map(child => {
              const isVisible = childrenVisibility[child.id] ?? true;

              if (!isVisible) {
                return null;
              }

              const itemType = getChildDisplayType(child);
              let status = "unknown";
              let title = child.title || "Item";

              if (child.itemType === "validation") {
                status = child.status || BoxStatus.INCOMPLETE;
              } else if (child.itemType === "generic") {
                title = child.title || `${child.genericType} item`;
              } else if (child.itemType === "bookmark") {
                title = "Bookmark";
              }

              return (
                <NwAccordionItem
                  key={child.id}
                  textSize="sm"
                  type="button"
                  withActiveIndicator={false}
                  label={child.itemType === "validation" ? getStatusLabel(status) : title}
                  textColor={this.store.focusedChildId.value === child.id ? "dark-gray" : "light-grey"}
                  isActive={false}
                  onClick={() => this.handleItemSelected(itemType, child.id)}
                >
                  {child.itemType === "validation" && <StatusIndicator status={status as BoxStatus} />}
                  {child.itemType === "bookmark" && <NWIcon name="bookmark" size="xs" />}
                  {child.itemType === "generic" && <NWIcon name="file" size="xs" />}
                </NwAccordionItem>
              );
            })}
          </NWAccordion>
        </div>
      );
    });
  }

  @bind
  private renderOpen(menuItems: TocItem[]) {
    const { isPinned } = this.store;

    return (
      <div
        class={classNames(
          "fixed top-1/2 right-0 w-fit transition-all ease-in-out duration-300 -translate-y-1/2 z-20 shrink-0 group-hover:translate-x-0 group-hover:delay-150",
          {
            "!right-0  translate-x-0": isPinned.value,
            "translate-x-[150%]": !isPinned.value,
          },
        )}
      >
        <div class="py-6 mr-4 sm:mr-6 border-gray-200 rounded-2xl shadow-md bg-white border max-w-xs">
          <button
            class={classNames(
              "absolute -top-2 -left-2 transition-all bg-white border hover:bg-secondary rounded-full p-2",
            )}
            onClick={this.togglePin}
          >
            <NWIcon
              name="pin"
              class={classNames("transition-all", {
                "text-leather": isPinned.value,
                "text-tertiary rotate-45": !isPinned.value,
              })}
            />
          </button>
          <ul class="overflow-y-auto max-h-[76vh] space-y-2 px-4 py-1 overscroll-contain w-full">
            {this.renderList(menuItems)}
          </ul>
        </div>
      </div>
    );
  }

  render() {
    const { items, focusedStepId } = this.store;
    const menuItems = items.value;

    if (!menuItems || menuItems?.length === 0) {
      return;
    }

    return (
      <>
        {/* Collapsed view */}
        <div class="block xl:hidden relative h-fit">
          <div class="group">
            {this.renderOpen(menuItems)}
            <div class={classNames("fixed right-0 top-1/2 p-4 pr-2 sm:pr-2 -translate-y-1/2")}>
              <ul class="animate-slideInRight space-y-4">
                {menuItems.map(({ id }) => (
                  <li
                    key={id}
                    onClick={() => this.handleItemSelected("step", id)}
                    class={classNames("w-5 h-0.5 cursor-pointer rounded-full transition-colors", {
                      "bg-brand-950": focusedStepId.value === id,
                      "bg-brand-200": focusedStepId.value !== id,
                    })}
                  />
                ))}
              </ul>
            </div>
          </div>
        </div>
        {/*Sidebar view*/}
        <div class="hidden xl:block sticky top-0 my-14">
          <div class="py-20">
            <div class="relative">
              <ul class="space-y-2 hide-scrollbar max-h-[65vh] overflow-y-auto animate-fadeIn z-50 px-6 min-w-48 w-full max-w-md">
                {this.renderList(menuItems)}
                <div
                  id="white-fade"
                  style="background: linear-gradient(180deg, rgba(255, 255, 255, 0.00) 0%, #FFF 100%)"
                  class={classNames(
                    "sticky -bottom-1 h-24 z-[51] w-full pointer-events-none transition-opacity duration-150",
                  )}
                ></div>
              </ul>
            </div>
          </div>
        </div>
      </>
    );
  }
}
